import React from 'react';
import { Ref, useRef, useEffect } from 'react';
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DayCardAssignmentContent } from './DayCardAssignmentContent';
import { DayCardActionGroup } from './DayCardActionGroup';
import { useNavigate, useParams } from 'react-router-dom';
import { LessonPlan, Assignment } from 'state/modules/entity/types';
import { useSelector } from 'react-redux';
import { AppStoreState, LESSON_PLANNER_MODE } from 'types';
import useDragDrop from 'state/hooks/ui/use-dragdrop';
import { getWeekStartingIndex } from 'util/lesson-plan-util';
import { NoInstruction } from './NoInstruction';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { NoClassroomsToAssignModal } from 'components/@common/Modals/NoClassroomsToAssignModal';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { getEpochTime } from 'util/date-util';
import { getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';
import { useTheme } from '@mui/material';

type Props = {
  handleMoveCard: (originIndex, destinationIndex) => void;
  cardIndex: number;
  lessonsOrAssignments: Array<LessonPlan | Assignment>;
  lessonDate: string;
  allowInstruction: boolean | undefined;
  className: string;
};

export const DayCard = ({
  handleMoveCard,
  cardIndex,
  lessonsOrAssignments,
  lessonDate,
  allowInstruction,
  ...props
}: Props) => {
  const { bookCode, edition } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles(theme)(lessonsOrAssignments && lessonsOrAssignments.length >= 2);

  const [, { openModal }] = useModal();
  const ref: Ref<any> = useRef(null);
  const entity: any = useSelector(({ entity }: AppStoreState) => entity);
  const currentLessonWeek = useSelector(
    ({ lessonPlanner }: AppStoreState) => lessonPlanner.currentlessonPlanWeek
  );
  const user = useSelector(({ user }: AppStoreState) => user);
  const mode = useSelector(({ lessonPlanner }: AppStoreState) => lessonPlanner.mode);
  const workbook = useSelector(({ app }: AppStoreState) => app.currentWorkbook);
  const classrooms: any = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const isValidTrialUser: boolean = useSelector(getLicenseIsTrial);
  const handleAssignLesson = () => {
    if (classrooms?.length) {
      const [{ productCurriculumId }] = workbook.products;
      const epochTime = getEpochTime(lessonDate);
      navigate(`/lesson-assignment/${bookCode}/${edition}/${productCurriculumId}/${epochTime}`);
    } else {
      openNoClassesToAssignModal(workbook?.gradeLevel, user);
    }
  };

  const isAssignmentView = () =>
    (lessonsOrAssignments as any)?.some(x => x?.studentIds?.length || x.classroomId);
  const isReadonly = () =>
    entity?.lessonPlan?.isActive === undefined ? false : !entity?.lessonPlan?.isActive;

  const getStartingCol = idx => idx + getWeekStartingIndex(currentLessonWeek);

  const openNoClassesToAssignModal = (gradeLevel, user) => {
    openModal({
      body: <NoClassroomsToAssignModal gradeLevel={gradeLevel} user={user} />,
      Title: () => <BaseModalTitle text={''} />
    });
  };

  const dragDropInputs = {
    ref,
    draggable: !classrooms?.length ? false : !isAssignmentView(),
    dragIndices: {
      col: getStartingCol(cardIndex),
      item: 0,
      itemId: 0
    }
  };

  const [{ endDrag, ...dragStates }, { drag, drop }] = useDragDrop(dragDropInputs);

  const maybeDraggableProps: any = lessonDate
    ? {
        ref: ref,
        style: {
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          border: dragStates.isOver ? '2px solid #2878C1' : '',
          opacity: dragStates.isDragging ? 0 : 1,
          fontSize: 25,
          fontWeight: 'bold'
        }
      }
    : undefined;

  useEffect(() => {
    drag(drop(ref));
  }, [drag, drop, ref]);

  return (
    <>
      <Paper elevation={2} {...props}>
        {lessonsOrAssignments && lessonsOrAssignments.length ? (
          lessonsOrAssignments?.map((lesson: any, lessonIndex) => (
            <DayCardAssignmentContent
              key={`${lessonIndex}-cardContent`}
              dragIndices={{
                col: cardIndex,
                item: lessonIndex,
                itemId: lesson.id
              }}
              lesson={lesson}
              classes={classes}
              lessonDate={lessonDate || lesson.date}
              readonly={isReadonly()}
            />
          ))
        ) : (
          <NoInstruction
            maybeDraggableProps={allowInstruction && maybeDraggableProps}
            allowInstruction={allowInstruction}
            mode={mode}
          />
        )}

        {!isAssignmentView() && lessonsOrAssignments && lessonsOrAssignments.length ? (
          <DayCardActionGroup
            className={classes.buttonGroup}
            handleAssignLesson={handleAssignLesson}
            disabled={
              mode === LESSON_PLANNER_MODE.ASSIGNMENTS || user.isAuditor || isValidTrialUser
            }
          />
        ) : null}
      </Paper>
    </>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    buttonGroup: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      marginTop: '15px',
      marginBottom: '25px',
      justifyContent: 'flex-end'
    },
    dayCardAssignment: multiLessonDay => ({
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      border: '1px solid #A6A6A6',
      borderRadius: 4,
      margin: 6,
      fontSize: '18px',
      minHeight: multiLessonDay ? '' : '400px'
    }),
    card: () => ({
      display: 'flex',
      flexDirection: 'column',
      fontFamily: theme.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 200,
      lineHeight: '24px',
      margin: '6px 10px'
    }),
    titleRow: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between'
    },
    button: {
      cursor: 'pointer',
      fontWeight: 400
    },
    titleClass: () => ({
      backgroundColor: `${theme.palette.primary.main} !important`
    }),
    alertActionBtn: {
      marginLeft: 10
    },
    chip: () => ({
      backgroundColor: `${theme.palette.primary.main} !important`,
      width: 'fit-content',
      color: '#fff !important',
      height: '22px',
      fontWeight: 400
    })
  }));
